import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getFeatureTipById, type FeatureTip } from './featureTipsCatalog';
import {
  dismissFeatureTip,
  FEATURE_TIPS_CHANGED_EVENT,
  getAllFeatureTips,
  getUndismissedFeatureTipCount,
  isFeatureTipDismissed,
} from './featureTipsPreference';
import { FeatureTipModal } from './FeatureTipModal';
import { FeatureTipsListModal } from './FeatureTipsListModal';

export interface FeatureTipsContextValue {
  undismissedCount: number;
  /** Open a specific tip (info icon / deep link). */
  openTip: (id: string) => void;
  /** Open Tips hub (header). */
  openTipsHub: () => void;
  /**
   * Auto-show once if not dismissed. Safe to call repeatedly;
   * only opens when the tip is still undismissed and not already shown this session.
   */
  maybeShowTip: (id: string) => void;
  close: () => void;
}

const FeatureTipsContext = createContext<FeatureTipsContextValue | null>(null);

type ModalMode = 'tip' | 'hub' | null;

export function FeatureTipsProvider({ children }: { children: ReactNode }) {
  const [undismissedCount, setUndismissedCount] = useState(getUndismissedFeatureTipCount);
  const [mode, setMode] = useState<ModalMode>(null);
  const [activeTip, setActiveTip] = useState<FeatureTip | null>(null);
  /** When tip was opened from the Tips hub, Close returns to the list. */
  const [openedFromHub, setOpenedFromHub] = useState(false);
  /** Tips auto-shown this browser session (avoid re-popping after Close without Got it). */
  const sessionAutoShownRef = useRef<Set<string>>(new Set());

  const refreshCount = useCallback(() => {
    setUndismissedCount(getUndismissedFeatureTipCount());
  }, []);

  useEffect(() => {
    const onChange = () => refreshCount();
    window.addEventListener(FEATURE_TIPS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(FEATURE_TIPS_CHANGED_EVENT, onChange);
  }, [refreshCount]);

  const close = useCallback(() => {
    setMode(null);
    setActiveTip(null);
    setOpenedFromHub(false);
  }, []);

  const openTip = useCallback((id: string) => {
    const tip = getFeatureTipById(id);
    if (!tip) return;
    setOpenedFromHub(false);
    setActiveTip(tip);
    setMode('tip');
  }, []);

  const openTipsHub = useCallback(() => {
    setActiveTip(null);
    setOpenedFromHub(false);
    setMode('hub');
  }, []);

  const maybeShowTip = useCallback((id: string) => {
    if (isFeatureTipDismissed(id)) return;
    if (sessionAutoShownRef.current.has(id)) return;
    const tip = getFeatureTipById(id);
    if (!tip) return;
    sessionAutoShownRef.current.add(id);
    // Let Look ahead layout settle before opening.
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        if (isFeatureTipDismissed(id)) return;
        setOpenedFromHub(false);
        setActiveTip(tip);
        setMode('tip');
      }, 180);
    });
  }, []);

  const handleDismissActive = useCallback(() => {
    if (!activeTip) return;
    dismissFeatureTip(activeTip.id);
    refreshCount();
    close();
  }, [activeTip, close, refreshCount]);

  const handleSelectFromHub = useCallback((tip: FeatureTip) => {
    setOpenedFromHub(true);
    setActiveTip(tip);
    setMode('tip');
  }, []);

  const handleCloseTip = useCallback(() => {
    if (openedFromHub) {
      setActiveTip(null);
      setOpenedFromHub(false);
      setMode('hub');
      return;
    }
    close();
  }, [openedFromHub, close]);

  const value = useMemo<FeatureTipsContextValue>(
    () => ({
      undismissedCount,
      openTip,
      openTipsHub,
      maybeShowTip,
      close,
    }),
    [undismissedCount, openTip, openTipsHub, maybeShowTip, close],
  );

  const tipShowDismiss = activeTip != null && !isFeatureTipDismissed(activeTip.id);

  return (
    <FeatureTipsContext.Provider value={value}>
      {children}
      <FeatureTipsListModal
        opened={mode === 'hub'}
        tips={getAllFeatureTips()}
        onClose={close}
        onSelectTip={handleSelectFromHub}
      />
      <FeatureTipModal
        opened={mode === 'tip' && activeTip != null}
        tip={activeTip}
        showDismiss={tipShowDismiss}
        onClose={handleCloseTip}
        onDismiss={handleDismissActive}
      />
    </FeatureTipsContext.Provider>
  );
}

export function useFeatureTips(): FeatureTipsContextValue {
  const ctx = useContext(FeatureTipsContext);
  if (!ctx) {
    throw new Error('useFeatureTips must be used within FeatureTipsProvider');
  }
  return ctx;
}

/** Optional hook when the provider may be absent (tests / isolated pages). */
export function useFeatureTipsOptional(): FeatureTipsContextValue | null {
  return useContext(FeatureTipsContext);
}
